//@ts-check
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, CircularProgress, Backdrop } from '@mui/material';

import payLinkService from '../../services/payLinkService';
import PaymentForm from './components/PaymentForm';
import PaymentConfirmationV2 from '../../components/PaymentConfirmationV2/PaymentConfirmationV2';
import InvoicePayLinkErrorBoundary from './components/InvoicePayLinkErrorBoundary';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import config from '../../constants/config';
import commonStrings from '../../constants/commonStrings';
const messageConstants = require('../../constants/message');
import locationsService from '../../services/locationsService';

/** @type {Record<string, TransactionStatuses[keyof TransactionStatuses]>} */
const INVOICE_STATUS = {
  PAID: 'Paid',
  SUBMITTED: 'Submitted',
  DELETED: 'Deleted',
};

/**
 * @component InvoicePayLink
 * @description Invoice payment link page for pre-loaded invoice payments.
 *
 * Features:
 * - Hash-based invoice resolution
 * - Customer data pre-loading
 * - Invoice status validation
 * - Card and ACH payment support
 * - Fee calculation
 * - reCAPTCHA validation
 *
 * @returns {JSX.Element} Invoice payment link page
 */
const InvoicePayLink = () => {
  const { id: invoiceHash } = useParams();

  // Loading and UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState(/** @type {string[]} */ ([]));
  const [warnings, setWarnings] = useState(/** @type {string[]} */ ([]));
  const [fieldErrors, setFieldErrors] = useState(/** @type {Record<string, string>} */ ({}));

  // Data state (fetched from API ViewModels)
  const [invoice, setInvoice] = useState(/** @type {InvoiceViewModel|null} */ (null));
  const [location, setLocation] = useState(/** @type {LocationViewModel|null} */ (null));
  const [customer, setCustomer] = useState(
    /** @type {CustomerViewModel|null} */ ({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    })
  );
  const [feeSettings, setFeeSettings] = useState(/** @type {DatabaseFeeSettings | null }*/ (null));
  const [termsConditions, setTermsConditions] = useState(/** @type {TermsConditions | null }*/ (null));

  // Payment success state
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(/** @type {PayLinkPaymentResponse|null} */ (null));
  const [calculationResult, setCalculationResult] = useState(/** @type {CalculationViewResult |null} */ (null));
  const [lastPaymentData, setLastPaymentData] = useState(/** @type {PaymentData | null} */ (null));

  // TSEP state (for Multipass gateway)
  const [tsepFieldSuccess, setTsepFieldSuccess] = useState(false);
  const [tsepFieldError, setTsepFieldError] = useState(/** @type {string | null} */ (null));

  /**
   * @description Deduplicated error handling based on HTTP status code
   * Renders warnings for 410/409 status, errors for others.
   *
   * @param {number} status - HTTP status code
   * @param {string} message - Error message
   */
  const handleErrorsByStatus = useCallback((status, message) => {
    /** @type {string[]} */
    const warningsList = [];
    /** @type {string[]} */
    const errorsList = [];

    if (message && (status === 410 || status === 409)) {
      warningsList.push(message);
    } else if (message) {
      errorsList.push(message);
    } else {
      errorsList.push(`${messageConstants.UNKNOWN_ERROR} ${messageConstants.CONTACT_SUPPORT}`);
    }

    if (warningsList.length > 0) {
      setWarnings((prev) => [...prev, ...warningsList]);
    }
    if (errorsList.length > 0) {
      setErrors((prev) => [...prev, ...errorsList]);
    }
  }, []);

  /**
   * @description Fetch location data via ViewModel (secure)
   * @param {string} locationId - Location ID
   */
  const fetchLocationData = useCallback(async (locationId) => {
    if (!locationId) return;

    try {
      /** @type {AxiosResponse} */
      const response = await payLinkService.getLocationViewModelById(locationId);
      const locationData = response.data?.location;

      if (locationData) {
        setLocation(locationData);
        localStorage.setItem('locationPaydetails', JSON.stringify(locationData));
      }
    } catch (err) {
      console.error('Error fetching location:', err);
      setErrors((prev) => [...prev, `Unable to load location data.`]);
    }
  }, []);

  /**
   * @description Fetch invoice data via ViewModel (secure)
   * @param {string} locationId - Location ID
   * @param {string} invoiceId - Invoice ID
   */
  const fetchInvoiceData = useCallback(
    async (locationId, invoiceId) => {
      if (!locationId || !invoiceId) return;

      try {
        /** @type {AxiosResponse} */
        const response = await payLinkService.getInvoiceViewModelById(locationId, invoiceId);
        const invoiceData = response.data?.invoice;

        if (invoiceData?._id) setInvoice(invoiceData);
      } catch (/** @type {any}*/ err) {
        if (err?.response?.status && err?.response?.data?.message) {
          handleErrorsByStatus(err?.response?.status, err?.response?.data?.message);
        }
      }
    },
    [handleErrorsByStatus]
  );

  /**
   * @description Resolve hash to locationId and invoiceId, then fetch invoice
   * @returns {boolean | void} isRedirecting - Indicates if the function is being called as part of a redirect process
   */
  const fetchLocationAndInvoiceIdsFromHash = useCallback(async () => {
    if (!invoiceHash) return;
    let isRedirecting = false;

    try {
      const hashResponse = await payLinkService.getLocationAndInvoiceIdsByHash(invoiceHash);
      const { invoiceId, locationId, redirectURL } = hashResponse.data || {};

      if (redirectURL) {
        isRedirecting = true;
        window.location.replace(redirectURL);
        return isRedirecting;
      }

      if (!invoiceId || !locationId) {
        setErrors([`Unable to resolve invoice.`]);
        return isRedirecting;
      }

      await fetchInvoiceData(locationId, invoiceId);
      return isRedirecting;
    } catch (/** @type {AxiosError | any} */ err) {
      handleErrorsByStatus(err?.response?.status, err?.response?.data?.message);
    }
  }, [invoiceHash, fetchInvoiceData, handleErrorsByStatus]);

  /**
   * @description Fetch user settings for fee calculation
   * @param {string} locationId - Location ID
   */
  const fetchUserSettings = useCallback(async (locationId) => {
    if (!locationId) return;

    try {
      /** @type {AxiosResponse} */
      const response = await payLinkService.getUserSettingsByLocationId(locationId);
      if (response.data?.userSettings) {
        setFeeSettings(response.data.userSettings);
      }
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('Error fetching user settings:', err);
    }
  }, []);

  /**
   * @description Fetch terms and conditions
   * @param {string} locationId - Location ID
   */
  const fetchTermsConditions = useCallback(async (locationId) => {
    if (!locationId) return;

    try {
      /** @type {AxiosResponse} */
      const response = await payLinkService.getTermsConditionsByLocationId(locationId);
      if (response.data?.termsConditions) {
        setTermsConditions(response.data.termsConditions);
      }
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('Error fetching terms:', err);
      setErrors((prev) => [...prev, `Unable to load terms and conditions.`]);
    }
  }, []);

  /**
   * @description Fetch customer data via ViewModel (secure)
   * @param {string} locationId - Location ID
   * @param {string} customerId - Customer ID
   */
  const fetchCustomerData = useCallback(async (locationId, customerId) => {
    if (!locationId || !customerId) return;

    try {
      /** @type {AxiosResponse} */
      const response = await payLinkService.getCustomerViewModelById(locationId, customerId);
      if (response.data?.customer) {
        setCustomer(response.data.customer);
      }
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('Error fetching customer:', err);
      // Non-critical - customer data is optional for one-time customers
    }
  }, []);

  /**
   * @description Fetch TSEP form data for Multipass gateway
   * @param {string} locationId - Location ID
   * @param {LocationViewModel} locationData - Current location data
   */
  const fetchTsepFormData = useCallback(async (locationId, locationData) => {
    if (!locationId || !locationData) return;

    try {
      /** @type {AxiosResponse} */
      const tsepResponse = await payLinkService.getTsepFormDataByLocationId(locationId);
      if (tsepResponse.data?.tsepFormData) {
        const updatedLocation = {
          ...locationData,
          multipassCredentials: tsepResponse.data.tsepFormData,
        };
        setLocation(updatedLocation);
        localStorage.setItem('locationPaydetails', JSON.stringify(updatedLocation));
      }
    } catch (/** @type {AxiosError | any} */ error) {
      console.error('Error fetching TSEP form data:', error);
    }
  }, []);

  /**
   * @description 1. Initial data load: Resolve hash to IDs and fetch invoice
   */
  useEffect(() => {
    if (isPaymentSuccess || !invoiceHash) return;

    const loadInitialData = async () => {
      setIsLoading(true);
      const isRedirecting = await fetchLocationAndInvoiceIdsFromHash();
      if (!isRedirecting) setIsLoading(false);
    };

    loadInitialData();
  }, [invoiceHash, isPaymentSuccess, fetchLocationAndInvoiceIdsFromHash]);

  /**
   * @description 2. Fetch location when invoice loads (if not paid/submitted/deleted)
   */
  useEffect(() => {
    if (isPaymentSuccess || !invoice?.locationId) return;

    const status = invoice?.STATUS;
    const shouldFetchLocation =
      !invoice.isPaid &&
      status !== INVOICE_STATUS.PAID &&
      status !== INVOICE_STATUS.SUBMITTED &&
      status !== INVOICE_STATUS.DELETED;

    if (shouldFetchLocation) {
      fetchLocationData(invoice.locationId);
    }
  }, [invoice?.locationId, invoice?.isPaid, invoice?.STATUS, isPaymentSuccess, fetchLocationData]);

  /**
   * @description 3. Fetch customer when invoice loads (excludes one-time customers)
   */
  useEffect(() => {
    if (isPaymentSuccess) return;
    if (!invoice?.customerId || invoice?.isOneTimeCustomer || !invoice?.locationId) return;

    fetchCustomerData(invoice.locationId, invoice.customerId);
  }, [invoice?.customerId, invoice?.isOneTimeCustomer, invoice?.locationId, isPaymentSuccess, fetchCustomerData]);

  /**
   * @description 4. Set one-time customer details from invoice
   */
  useEffect(() => {
    if (isPaymentSuccess) return;
    if (invoice?.isOneTimeCustomer && invoice?.oneTimeCustomerDetails?.firstName) {
      /** @type {CustomerViewModel} */
      const oneTimeCustomer = {
        firstName: invoice.oneTimeCustomerDetails.firstName,
        lastName: invoice.oneTimeCustomerDetails.lastName || '',
        email: invoice.oneTimeCustomerDetails.email || '',
        phone: invoice.oneTimeCustomerDetails.phone || '',
      };
      setCustomer(oneTimeCustomer);
    }
  }, [invoice?.isOneTimeCustomer, invoice?.oneTimeCustomerDetails, isPaymentSuccess]);

  /**
   * @description 5. Fetch terms/conditions and user settings once location is loaded
   */
  useEffect(() => {
    if (isPaymentSuccess || !location?._id) return;
    Promise.all([fetchTermsConditions(location._id), fetchUserSettings(location._id)]);
  }, [location?._id, isPaymentSuccess, fetchTermsConditions, fetchUserSettings]);

  /**
   * @description 6. Initialize payment gateway services once location gateway is known
   */
  useEffect(() => {
    if (isPaymentSuccess || !location?.gateway) return;

    // @ts-ignore
    if (location.gateway === commonStrings.paytrace && window.paytrace) {
      // @ts-ignore
      window.paytrace.setKeyAjax(payLinkService.getPublicKeyUri(location._id));
    } else if (location.gateway === commonStrings.multipass) {
      fetchTsepFormData(location._id, location);
    }
  }, [location?.gateway, location?._id, isPaymentSuccess, fetchTsepFormData]);

  /**
   * @description Get reCAPTCHA token for payment submission
   * @returns {Promise<string|null>} reCAPTCHA token or null if failed
   */
  const getRecaptchaToken = useCallback(async () => {
    // @ts-ignore
    if (!window.grecaptcha) {
      setErrors([`Unable to process payment, CAPTCHA could not load.`]);
      return null;
    }

    try {
      // @ts-ignore
      const token = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_KEY, { action: 'submit' });
      if (!token || typeof token !== 'string') {
        setErrors([`Unable to process payment, CAPTCHA could not load.`]);
        return null;
      }
      return token;
    } catch (/** @type {AxiosError | any} */ err) {
      console.error('reCAPTCHA error:', err);
      setErrors([`Unable to process payment, CAPTCHA could not load.`]);
      return null;
    }
  }, []);

  /**
   * @description Handle payment form submission
   * @param {PaymentData} paymentData - Payment data from PaymentForm component
   */
  const handleSubmit = useCallback(
    async (paymentData) => {
      setErrors([]);
      setIsSubmitting(true);

      try {
        // Validate invoice status
        if (
          invoice?.isPaid ||
          invoice?.STATUS === INVOICE_STATUS.PAID ||
          invoice?.STATUS === INVOICE_STATUS.SUBMITTED
        ) {
          setErrors(['Invoice has already been paid.']);
          setIsSubmitting(false);
          return;
        }

        if (!invoice?._id || invoice?.STATUS === INVOICE_STATUS.DELETED) {
          setErrors(['Invoice has been deleted.']);
          setIsSubmitting(false);
          return;
        }

        if (!location?._id) {
          setErrors(['Location data is missing. Cannot process payment.']);
          setIsSubmitting(false);
          return;
        }

        // Get reCAPTCHA token
        const recaptchaToken = await getRecaptchaToken();
        if (!recaptchaToken) {
          setIsSubmitting(false);
          return;
        }

        const invoicePaymentPayload = {
          paymentMethod: paymentData.paymentMethod,
          billingAddress: paymentData.billingAddress,
          shippingAddress: paymentData.shippingAddress,
          recaptchaToken,
        };

        // Submit payment
        const response = await payLinkService.runInvoicePayLinkPayment(
          invoice._id,
          location._id,
          invoicePaymentPayload
        );

        // Store data for confirmation display
        setCalculationResult(paymentData.calculationResult);
        setLastPaymentData(paymentData);

        // Handle success
        setIsPaymentSuccess(true);
        setPaymentResponse({
          message: response?.data?.message,
          transactionId: response?.data?.transactionId,
        });
      } catch (/** @type {AxiosError | any} */ err) {
        if (err?.response?.status && err?.response?.data?.message) {
          handleErrorsByStatus(err?.response?.status, err?.response?.data?.message);
        } else {
          setErrors([`Payment failed.`]);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [invoice, location, getRecaptchaToken]
  );

  /**
   * @description Handle invoice PDF download
   */
  const handleViewInvoice = useCallback(() => {
    if (!invoice?.invoice) return;
    const path = `${config.BASEURL}downloadInvoiceFile/${invoice.invoice}`;
    window.open(path, '_blank');
  }, [invoice?.invoice]);

  /**
   * @description Handle policy PDF download
   * @param {string} policyType - Type of policy to view
   */
  const handleViewPolicy = useCallback(
    async (policyType) => {
      if (termsConditions && termsConditions[/** @type {keyof TermsConditions} */ (policyType)]) {
        let blobUrl = '';
        try {
          if (!location?._id) throw new Error('Location ID is missing. Cannot fetch policy document.');
          const response = await payLinkService.getPolicyFile(location._id, policyType);
          const blob = new Blob([response.data], { type: 'application/pdf' });
          blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
        } catch (error) {
          console.error('Policy viewing error:', error);
          setErrors((prev) => [...prev, 'Failed to load the policy document. Please try again.']);
        } finally {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
        }
      } else {
        setErrors((prev) => [...prev, 'The requested policy document is not available.']);
      }
    },
    [location?._id, termsConditions]
  );

  /**
   * @description Remove alert message at index
   * @param {number} index - Index of alert to remove
   * @param {React.Dispatch<React.SetStateAction<string[]>>} setter - State setter function for the alert array
   */
  const removeAlert = (index, setter) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // Show loading spinner while fetching initial data
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 0, px: 0 }}>
        <Backdrop open={true} sx={{ zIndex: 9999, color: '#fff' }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 0, px: 0 }}>
      {/* Loading Backdrop */}
      <Backdrop open={isSubmitting} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Warning Messages */}
      {warnings.length > 0 && (
        <FlexAlerts
          messages={warnings.map((message, index) => ({
            severity: AlertSeverityTypes.warning,
            message,
            action: {
              text: 'Close',
              onClick: () => removeAlert(index, setWarnings),
              'data-cy': `InvoicePayLink-warning-alert-close-${index}`,
            },
          }))}
        />
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <FlexAlerts
          messages={errors.map((message, index) => ({
            severity: AlertSeverityTypes.error,
            message,
            action: {
              text: 'Close',
              onClick: () => removeAlert(index, setErrors),
              'data-cy': `InvoicePayLink-error-alert-close-${index}`,
            },
          }))}
        />
      )}

      {/* Payment Success */}
      {isPaymentSuccess && invoice?.STATUS !== INVOICE_STATUS.DELETED && paymentResponse && lastPaymentData && (
        <PaymentConfirmationV2
          invoiceNumber={invoice?.invoice}
          paymentResponse={paymentResponse}
          paymentData={lastPaymentData}
          customerName={`${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()}
          calculationResult={calculationResult}
          locationName={location?.locationName}
          locationImageUrl={locationsService.getLocationImageByIdUrl(location?._id || '')}
          feeSettings={feeSettings}
          onDownloadInvoice={handleViewInvoice}
        />
      )}

      {/* Payment Form */}
      {!isPaymentSuccess && invoice?.STATUS !== INVOICE_STATUS.DELETED && !invoice?.isPaid && invoice && location && (
        <PaymentForm
          location={location}
          invoice={invoice}
          customer={customer}
          feeSettings={feeSettings}
          termsConditions={termsConditions}
          baseAmount={invoice?.amount?.toString() || '0.00'}
          onSubmit={handleSubmit}
          onViewInvoice={handleViewInvoice}
          onViewPolicy={handleViewPolicy}
          isLoading={isSubmitting}
          setErrors={setErrors}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
          showInvoiceDetails={true}
          firstName={customer?.firstName || ''}
          lastName={customer?.lastName || ''}
          tsepFieldSuccess={tsepFieldSuccess}
          setTsepFieldSuccess={setTsepFieldSuccess}
          tsepFieldError={tsepFieldError}
          setTsepFieldError={setTsepFieldError}
        />
      )}
    </Container>
  );
};

/**
 * @component InvoicePayLinkWithErrorBoundary
 * @description InvoicePayLink wrapped with error boundary for graceful error handling
 * @returns {JSX.Element} Wrapped component
 */
const InvoicePayLinkWithErrorBoundary = () => (
  <InvoicePayLinkErrorBoundary>
    <InvoicePayLink />
  </InvoicePayLinkErrorBoundary>
);

export default InvoicePayLinkWithErrorBoundary;

/** @typedef {import('../../../../types/PaymentConfirmationV2').PaymentResponseData} PaymentResponseData */
/** @typedef {import('../../../../types/transactions').TransactionStatuses} TransactionStatuses */

/** @typedef {import('axios').AxiosPromise} AxiosPromise */
/** @typedef {import('axios').AxiosResponse} AxiosResponse */
/** @typedef {import('axios').AxiosError} AxiosError */
/** @typedef {import('../../../../types/locations').LocationViewModel} LocationViewModel */
/** @typedef {import('../../../../types/invoices').InvoiceViewModel} InvoiceViewModel */
/** @typedef {import('../../../../types/paymentDataBuilder').PaymentData} PaymentData */
/** @typedef {import('../../../../types/termsConditions').TermsConditions} TermsConditions */
/** @typedef {import('../../../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings */
/** @typedef {import('../../../../types/fees').CalculationViewResult} CalculationViewResult */
/** @typedef {import('../../../../types/customers').CustomerViewModel} CustomerViewModel */
/** @typedef {import('../../../../types/common').PayLinkPaymentResponse} PayLinkPaymentResponse */
